import useCommandFlowStore from "../../../store/commandFlowStore";
import { handleSpecialOutput } from "../../../utils/core/commandFlowUtils";
import { clearSessionState } from "../../../utils/session/storeStateUtils";
import {
  logRedirectNoPrompt,
  logRedirectSpecialOutput,
  logSessionStateClearing,
} from "../../../utils/core/debugUtils";
import { displayRedirectCompletionOutput } from "./redirectOutputHandlers";

/**
 * Handles redirect command result when it has no prompt (command completed)
 * @param {Object} redirectResult - The redirect command result
 * @param {Object} params - Parameters containing setters and callbacks
 */
export async function handleRedirectCompletion(redirectResult, params) {
  const { setters, engine, initMessage, onCommandComplete, nextCommandName } =
    params;

  logRedirectNoPrompt();

  // Check for special output first
  if (
    await handleSpecialOutput({
      result: redirectResult,
      setCurrentPrompt: setters.setCurrentPrompt,
      setInSession: useCommandFlowStore.getState().setInSession,
      initMessage,
      onCommandComplete,
      incrementResetToken: setters.incrementResetToken,
      engine,
    })
  ) {
    logRedirectSpecialOutput();
    return;
  }

  // Handle regular output from completed command
  if (redirectResult.output) {
    await displayRedirectCompletionOutput(redirectResult.output);
  }

  // Clear session state since command completed
  clearRedirectSessionState(onCommandComplete, engine, nextCommandName);
}

/**
 * Clears session state after redirect command completion
 * @param {Function} onCommandComplete - Callback for command completion
 * @param {Object} engine - The command flow engine to end the session on
 * @param {string} nextCommandName - The name of the redirect command (passed from caller)
 */
export function clearRedirectSessionState(
  onCommandComplete,
  engine,
  nextCommandName
) {
  logSessionStateClearing();

  // Get command name and session data BEFORE ending the session
  // Use nextCommandName from params since engine session may already be ended
  let commandName = nextCommandName;
  let sessionData = {};

  if (engine && engine.sessionManager) {
    // Try to get from engine if still available
    if (!commandName) {
      commandName = engine.sessionManager.currentCommandName;
    }
    sessionData = engine.sessionManager.session?.data || {};
  }

  // End the engine session
  if (engine && typeof engine.endSession === "function") {
    engine.endSession();
  }

  // Then clear the store state and call completion callback with command info
  clearSessionState(onCommandComplete, commandName, sessionData);
}
